import { useState, useEffect } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { useAuth } from "@/context/auth/auth-context"
import { Analytics } from "@/lib/analytics"
import hive from "@/lib/api/hive"
import { GoogleLogin } from "@react-oauth/google"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Loader2 } from "lucide-react"
import { cn } from "@/lib/utils"
import { useTheme } from "@/context/theme/theme-context"

export default function AuthPage() {
	const location = useLocation()
	const navigate = useNavigate()
	const { login } = useAuth()
	const { theme } = useTheme()

	// Tabs state
	const [activeTab, setActiveTab] = useState<"login" | "signup">("login")

	useEffect(() => {
		if (location.pathname === "/signup") setActiveTab("signup")
		else setActiveTab("login")
	}, [location.pathname])

	// Form State
	const [isLoading, setIsLoading] = useState(false)
	const [error, setError] = useState("")

	// Fields
	const [email, setEmail] = useState("")
	const [password, setPassword] = useState("")
	const [firstName, setFirstName] = useState("")
	const [lastName, setLastName] = useState("")

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		setError("")
		setIsLoading(true)

		try {
			if (activeTab === "login") {
				const response = await hive.login({ email, password })
				await login(response)
			} else {
				const response = await hive.signup({
					email,
					password,
					first_name: firstName,
					last_name: lastName,
				})
				await login(response, true)
			}

			const from = location.state?.from?.pathname || "/dashboard"

			navigate(from, { replace: true })
		} catch (err: unknown) {
			console.error(err)
			const msg = err instanceof Error ? err.message : "Authentication failed"
			setError(msg)
			Analytics.capture("Auth Failed", { type: activeTab, error: msg })
		} finally {
			setIsLoading(false)
		}
	}

	const switchTab = (tab: "login" | "signup") => {
		setActiveTab(tab)
		setError("")

		navigate(`/${tab}`, {
			replace: true,
			state: location.state,
		})
	}

	const handleGoogleSuccess = async (credential: string) => {
		setError("")
		setIsLoading(true)
		try {
			const response = await hive.socialLogin("google", credential)
			await login(response, response.is_new_user)
			const from = location.state?.from?.pathname || "/dashboard"
			navigate(from, { replace: true })
		} catch (err: unknown) {
			const msg = err instanceof Error ? err.message : "Google sign-in failed"
			setError(msg)
			Analytics.capture("Auth Failed", { type: "google", error: msg })
		} finally {
			setIsLoading(false)
		}
	}

	return (
		<div className="flex items-center justify-center min-h-[80vh] px-4">
			<Card className="w-full max-w-sm bg-white/60 dark:bg-black/20 backdrop-blur-md border-sky-900/10 transition-all duration-300">
				<CardHeader>
					<div className="flex items-center justify-between mb-2">
						<CardTitle className="text-2xl text-sky-950 dark:text-sky-50">
							{activeTab === "login" ? "Welcome back" : "Join the Hive"}
						</CardTitle>
					</div>
					<CardDescription>
						{activeTab === "login"
							? "Enter your credentials to access your hive."
							: "Create an account to start tracking."}
					</CardDescription>

					{/* TABS */}
					<div className="grid grid-cols-2 gap-1 p-1 mt-4 bg-sky-100/50 dark:bg-sky-900/20 rounded-lg">
						<button
							type="button"
							onClick={() => switchTab("login")}
							className={cn(
								"text-sm font-medium py-1.5 rounded-md transition-all",
								activeTab === "login"
									? "bg-white dark:bg-sky-950 text-sky-600 shadow-sm"
									: "text-muted-foreground hover:text-sky-600",
							)}
						>
							Login
						</button>
						<button
							type="button"
							onClick={() => switchTab("signup")}
							className={cn(
								"text-sm font-medium py-1.5 rounded-md transition-all",
								activeTab === "signup"
									? "bg-white dark:bg-sky-950 text-sky-600 shadow-sm"
									: "text-muted-foreground hover:text-sky-600",
							)}
						>
							Sign Up
						</button>
					</div>
				</CardHeader>

				<form onSubmit={handleSubmit}>
					<CardContent className="space-y-4">
						{error && (
							<Alert variant="destructive" className="py-2 text-sm">
								<AlertDescription>{error}</AlertDescription>
							</Alert>
						)}

						{/* SIGNUP FIELDS */}
						{activeTab === "signup" && (
							<div className="grid grid-cols-1 sm:grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-2 duration-300">
								<div className="space-y-2">
									<Label htmlFor="fn">First Name</Label>
									<Input
										id="fn"
										value={firstName}
										onChange={(e) => setFirstName(e.target.value)}
										required={activeTab === "signup"}
									/>
								</div>
								<div className="space-y-2">
									<Label htmlFor="ln">Last Name</Label>
									<Input id="ln" value={lastName} onChange={(e) => setLastName(e.target.value)} />
								</div>
							</div>
						)}

						<div className="space-y-2">
							<Label htmlFor="email">Email</Label>
							<Input
								id="email"
								type="email"
								placeholder="beekeeper@example.com"
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								required
							/>
						</div>
						<div className="space-y-2">
							<Label htmlFor="password">Password</Label>
							<Input
								id="password"
								type="password"
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								required
								minLength={8}
							/>
							{activeTab === "signup" && (
								<p className="text-xs text-muted-foreground">Must be at least 8 characters</p>
							)}
						</div>
					</CardContent>

					<CardFooter className="flex flex-col gap-4">
						<Button className="w-full bg-sky-600 hover:bg-sky-700 text-white" disabled={isLoading}>
							{isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
							{activeTab === "login" ? "Login" : "Create Account"}
						</Button>

						<div className="relative w-full flex items-center gap-3">
							<div className="flex-1 h-px bg-border" />
							<span className="text-xs text-muted-foreground shrink-0">or continue with</span>
							<div className="flex-1 h-px bg-border" />
						</div>

						<div className="w-full flex justify-center">
							<GoogleLogin
								onSuccess={(response) => {
									if (response.credential) handleGoogleSuccess(response.credential)
								}}
								onError={() => setError("Google sign-in failed. Please try again.")}
								theme={theme === "dark" ? "filled_black" : "outline"}
								text={activeTab === "login" ? "signin_with" : "signup_with"}
								shape="rectangular"
								width="100%"
							/>
						</div>
					</CardFooter>
				</form>
			</Card>
		</div>
	)
}
